/**
 * Spin Wheel — refined game UX (PRD §18, post-audit overhaul).
 *
 * Built on the shared game foundation (`games/_shared/*`): framed branded
 * GameStage, GameButton, sound, haptics, confetti celebration. Canvas render
 * with HiDPI scaling, prize NAMES on every segment (audit fix), and segment
 * colours that stay distinct against any brand via `ensureContrast` (fixes the
 * "dark brand vanishes into the stage" bug).
 *
 * `WheelItem` + `pickPrize` are re-exported for legacy callers.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/lib/i18n/useT';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { pickPrize as sharedPickPrize, type WheelItem } from './games/pickPrize';
import { fillSlots, slotsFor } from './games/fillSlots';
import { GameStage } from './games/_shared/GameStage';
import { GameButton } from './games/_shared/GameButton';
import { useGameSound } from './games/_shared/useGameSound';
import { useHaptics } from './games/_shared/useHaptics';
import { useCelebration } from './games/_shared/Celebration';
import { usePrefersReducedMotion } from './games/_shared/usePrefersReducedMotion';
import {
  ensureContrast,
  readableText,
  shade,
  tint,
  withAlpha,
} from './games/_shared/colors';

// Legacy re-exports — older imports may pull these from this module.
export type { WheelItem };
export const pickPrize = sharedPickPrize;

interface WheelGameProps {
  items: WheelItem[];
  primary: string;
  accent: string;
  onWin: (item: WheelItem) => void;
}

const SIZE = 320; // logical px (scaled by devicePixelRatio for crispness)
const CX = SIZE / 2;
const CY = SIZE / 2;
const R = SIZE / 2 - 6;
const HR = R * 0.2; // hub radius
const RW = R * 0.05; // rim width

type Mode = 'idle' | 'spin' | 'reveal' | 'done';

export function WheelGame({ items, primary, accent, onWin }: WheelGameProps) {
  const t = useT();
  const sound = useGameSound();
  const { vibrate } = useHaptics();
  const celebration = useCelebration();
  const reduced = usePrefersReducedMotion();

  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  // Task 4.6 — pad to six segments by repeating prizes in turn. Odds are still
  // drawn over `valid`; a repeated prize just has more than one place to land.
  const slots = useMemo(() => fillSlots(valid), [valid]);
  const N = Math.max(slots.length, 1);
  const ARC = (Math.PI * 2) / N;

  // Two segment colours guaranteed to read against each other and the stage.
  const segColors = useMemo(() => {
    const a = ensureContrast(accent, shade(primary, 0.3), 1.5);
    const b = ensureContrast(tint(primary, 0.18), accent, 1.5);
    return [a, b];
  }, [primary, accent]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rotRef = useRef(0);
  const modeRef = useRef<Mode>('idle');
  const winnerIdxRef = useRef<number>(-1);
  const revealTRef = useRef(0);
  const idleTRef = useRef(0);
  const tickRef = useRef({ prev: -1, flash: 0 });
  const rafRef = useRef<number | null>(null);
  const [, force] = useState(0);

  const images = useMemo(() => {
    if (typeof Image === 'undefined') return [] as (HTMLImageElement | null)[];
    return valid.map((item) => {
      const src = item.logo ?? item.image ?? null;
      if (!src) return null;
      const el = new Image();
      // No crossOrigin: /uploads doesn't send Access-Control-Allow-Origin, so
      // an 'anonymous' request fails CORS and the logo never loads (the wheel
      // then falls back to the prize NAME). We only draw these images to the
      // canvas — never read pixels back — so the resulting taint is harmless.
      el.src = resolveImageUrl(src);
      return el;
    });
  }, [valid]);

  const segAtPointer = (r: number) => {
    const a = ((-Math.PI / 2 - r) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    return Math.floor(a / ARC) % N;
  };

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2.5);
    cv.width = SIZE * dpr;
    cv.height = SIZE * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const draw = () => {
      const r = rotRef.current;
      const mode = modeRef.current;
      const wsi = winnerIdxRef.current;
      ctx.clearRect(0, 0, SIZE, SIZE);

      // Outer glow
      const bg = ctx.createRadialGradient(CX, CY, R * 0.55, CX, CY, R + 20);
      bg.addColorStop(0, withAlpha(accent, 0.22));
      bg.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(CX, CY, R + 20, 0, Math.PI * 2);
      ctx.fillStyle = bg;
      ctx.fill();

      // Segments
      ctx.save();
      ctx.translate(CX, CY);
      ctx.rotate(r);
      for (let i = 0; i < N; i++) {
        const a0 = i * ARC;
        const a1 = a0 + ARC;
        const am = a0 + ARC / 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, R - RW, a0, a1);
        ctx.closePath();
        const color = segColors[i % 2];
        const gx = Math.cos(am) * (R - RW) * 0.6;
        const gy = Math.sin(am) * (R - RW) * 0.6;
        const grad = ctx.createRadialGradient(gx * 0.3, gy * 0.3, R * 0.06, gx, gy, R * 0.8);
        grad.addColorStop(0, tint(color, 0.12));
        grad.addColorStop(1, shade(color, 0.82));
        ctx.fillStyle = grad;
        ctx.fill();

        if (mode === 'reveal' && i === wsi) {
          const alpha = 0.12 + 0.1 * Math.sin(revealTRef.current * 9);
          ctx.fillStyle = `rgba(255,255,255,${alpha})`;
          ctx.fill();
        }
        ctx.strokeStyle = 'rgba(255,255,255,.22)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Prize name + optional image, centred on the segment and kept
        // upright (left-hemisphere labels are flipped so none read inverted).
        const slot = slots[i];
        const item = slot?.item;
        if (item) {
          const txtColor = readableText(color);
          const flip = am > Math.PI / 2 && am < Math.PI * 1.5;
          const img = images[slot.sourceIndex];
          const hasImg = img && img.complete && img.naturalWidth > 0;
          ctx.save();
          ctx.translate(Math.cos(am) * (R - RW) * 0.6, Math.sin(am) * (R - RW) * 0.6);
          ctx.rotate(flip ? am + Math.PI : am);
          if (hasImg) {
            // The xotski logo/image IS the segment content (matches the design
            // wheel). No name on the slice — the prize name is revealed on the
            // win screen.
            const sz = Math.max(24, Math.min(54, 70 - N * 4));
            ctx.beginPath();
            ctx.arc(0, 0, sz / 2 + 2, 0, Math.PI * 2);
            ctx.fillStyle = '#fff';
            ctx.fill();
            ctx.save();
            ctx.beginPath();
            ctx.arc(0, 0, sz / 2, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img as HTMLImageElement, -sz / 2, -sz / 2, sz, sz);
            ctx.restore();
          } else {
            // No logo on this prize → fall back to its name.
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = txtColor;
            const fontSize = Math.max(9, Math.min(14, 16 - N));
            ctx.font = `700 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
            ctx.shadowColor = withAlpha('#000000', 0.35);
            ctx.shadowBlur = 2;
            const label = fitLabel(ctx, item.name, (R - RW) * 0.78);
            ctx.fillText(label, 0, 0);
            ctx.shadowBlur = 0;
          }
          ctx.restore();
        }
      }

      if (mode === 'spin' && tickRef.current.flash > 0) {
        ctx.save();
        ctx.rotate(-r);
        ctx.beginPath();
        ctx.arc(0, -(R - RW) * 0.93, 6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(tickRef.current.flash / 9) * 0.55})`;
        ctx.fill();
        tickRef.current.flash--;
        ctx.restore();
      }
      ctx.restore();

      // Outer rim
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.strokeStyle = shade(accent, 0.5);
      ctx.lineWidth = RW;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(CX, CY, R - RW, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255,255,255,.18)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(CX, CY, R + RW * 0.35, 0, Math.PI * 2);
      ctx.strokeStyle = withAlpha(accent, 0.3);
      ctx.lineWidth = RW * 0.7;
      ctx.stroke();

      // Hub
      const hPulse =
        mode === 'idle'
          ? 1 + 0.02 * Math.sin(idleTRef.current * 2.1)
          : mode === 'reveal'
            ? 1 + 0.06 * Math.sin(revealTRef.current * 11)
            : 1;
      ctx.save();
      ctx.translate(CX, CY);
      ctx.scale(hPulse, hPulse);
      const hg = ctx.createRadialGradient(0, 0, HR * 0.85, 0, 0, HR + 14);
      hg.addColorStop(0, withAlpha(accent, 0.22));
      hg.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(0, 0, HR + 14, 0, Math.PI * 2);
      ctx.fillStyle = hg;
      ctx.fill();
      const hb = ctx.createRadialGradient(-HR * 0.32, -HR * 0.32, 0, 0, 0, HR);
      hb.addColorStop(0, 'rgba(255,255,255,.85)');
      hb.addColorStop(0.45, accent);
      hb.addColorStop(1, shade(accent, 0.55));
      ctx.beginPath();
      ctx.arc(0, 0, HR, 0, Math.PI * 2);
      ctx.fillStyle = hb;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,.5)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.font = '800 11px ui-sans-serif, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = readableText(accent);
      ctx.fillText(t('publicSurvey.games.wheel.hub'), 0, 0.5);
      ctx.restore();

      // Pointer (fixed, top center, pointing down into the wheel)
      const PW = 16;
      const PH = 26;
      ctx.save();
      ctx.translate(CX, 1);
      ctx.shadowColor = withAlpha('#000000', 0.5);
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 2;
      ctx.beginPath();
      ctx.moveTo(-PW / 2, 0);
      ctx.lineTo(PW / 2, 0);
      ctx.lineTo(0, PH);
      ctx.closePath();
      ctx.fillStyle = tint(accent, 0.15);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      ctx.strokeStyle = 'rgba(255,255,255,.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      // Specular highlight
      const sp = ctx.createRadialGradient(CX - R * 0.12, CY - R * 0.38, 0, CX, CY, R * 0.95);
      sp.addColorStop(0, 'rgba(255,255,255,.09)');
      sp.addColorStop(0.45, 'rgba(255,255,255,.02)');
      sp.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(CX, CY, R, 0, Math.PI * 2);
      ctx.fillStyle = sp;
      ctx.fill();

      if (mode === 'spin') {
        const cur = segAtPointer(r);
        if (cur !== tickRef.current.prev) {
          tickRef.current.prev = cur;
          tickRef.current.flash = 7;
          sound.play('tick');
          vibrate('tick');
        }
      }
    };

    const loop = () => {
      idleTRef.current += 0.05;
      if (modeRef.current === 'reveal') revealTRef.current += 0.05;
      draw();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [N, ARC, segColors, images, slots, t]);

  const spin = () => {
    if (modeRef.current !== 'idle' || valid.length === 0) return;
    const prizeIdx = sharedPickPrize(valid);
    if (prizeIdx < 0) return;
    // Land on one of the segments showing that prize — picking the segment
    // after the prize keeps the odds in `pickPrize` and off the geometry.
    const candidates = slotsFor(slots, prizeIdx);
    const winnerIdx = candidates[Math.floor(Math.random() * candidates.length)] ?? 0;
    winnerIdxRef.current = winnerIdx;
    modeRef.current = 'spin';
    sound.play('spinUp');
    vibrate('bump');

    const targetCenter = (winnerIdx + 0.5) * ARC;
    const targetRot = -Math.PI / 2 - targetCenter;
    const targetNorm = ((targetRot % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const curNorm = ((rotRef.current % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    let delta = (targetNorm - curNorm + Math.PI * 2) % (Math.PI * 2);
    if (delta < 0.25) delta += Math.PI * 2;
    const spins = (reduced ? 2 : 7 + Math.random() * 3) * Math.PI * 2;
    const total = spins + delta;
    const start = rotRef.current;
    const dur = reduced ? 900 : 4400 + Math.random() * 2200;
    const t0 = performance.now();
    const ease = (x: number) => 1 - Math.pow(1 - x, 4);
    force((f) => f + 1);

    const animate = (now: number) => {
      const tt = Math.min((now - t0) / dur, 1);
      rotRef.current = start + total * ease(tt);
      if (tt < 1) {
        requestAnimationFrame(animate);
      } else {
        rotRef.current = start + total;
        modeRef.current = 'reveal';
        revealTRef.current = 0;
        celebration.fire();
        sound.play('win');
        vibrate('win');
        setTimeout(
          () => {
            modeRef.current = 'done';
            force((f) => f + 1);
            onWin(valid[prizeIdx]);
          },
          reduced ? 700 : 1350,
        );
      }
    };
    requestAnimationFrame(animate);
  };

  if (valid.length === 0) {
    return <GameEmpty />;
  }

  const isSpinning = modeRef.current === 'spin' || modeRef.current === 'reveal';

  return (
    <GameStage
      primary={primary}
      accent={accent}
      sound={sound}
      celebration={celebration}
      footer={
        <GameButton accent={accent} busy={isSpinning} onClick={spin}>
          {isSpinning ? t('publicSurvey.games.wheel.busy') : t('publicSurvey.games.wheel.action')}
        </GameButton>
      }
    >
      <canvas
        ref={canvasRef}
        onClick={spin}
        className="w-full max-w-[340px] cursor-pointer select-none"
        style={{ aspectRatio: '1 / 1', WebkitTapHighlightColor: 'transparent' }}
      />
    </GameStage>
  );
}

/** Trim a label so it fits within `maxW` px, adding an ellipsis if needed. */
function fitLabel(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let lo = 1;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(text.slice(0, mid) + '…').width <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + '…';
}

function GameEmpty() {
  const t = useT();
  return (
    <div className="rounded-2xl bg-black/40 p-8 text-center">
      <p className="font-bold">{t('publicSurvey.wheel.emptyTitle')}</p>
      <p className="mt-1 text-sm opacity-70">{t('publicSurvey.wheel.emptyBody')}</p>
    </div>
  );
}
